'use client';

import { Component, type ReactNode } from 'react';

/**
 * Contains Leaflet crashes to the map panel. In production the map can throw
 * during remount races — "Map container is being reused by another instance",
 * pane `appendChild` on a torn-down map — and without a boundary here that
 * error bubbles to the route error boundary and replaces the entire store
 * locator with the error screen.
 *
 * First failure: remount the map once on a fresh subtree — the key change
 * gives Leaflet a brand-new container element, clearing the stale
 * `_leaflet_id` that causes the reuse error. Second failure: give up on the
 * map and show a quiet placeholder; search and the store list stay usable.
 */
export class StoreMapBoundary extends Component<
	{ children: ReactNode },
	{ attempt: number; failed: boolean }
> {
	state = { attempt: 0, failed: false };

	static getDerivedStateFromError() {
		return { failed: true };
	}

	componentDidCatch(error: Error) {
		if (this.state.attempt === 0) {
			// First failure auto-recovers below — warn, don't error, so the dev
			// overlay doesn't surface a crash that is about to heal itself.
			console.warn('[store-map] Map crashed — remounting it once to recover.', error);
			// Let the broken tree unmount fully before mounting the retry.
			setTimeout(() => this.setState({ attempt: 1, failed: false }), 0);
			return;
		}
		console.error('[store-map] Map crashed again — giving up on the map panel.', error);
	}

	render() {
		if (this.state.failed) {
			return (
				<div className="flex h-full w-full items-center justify-center bg-neutral-100 px-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-neutral-400">
					{this.state.attempt === 0
						? 'Loading map…'
						: 'Map unavailable — browse the stores in the list'}
				</div>
			);
		}

		return (
			<div key={this.state.attempt} className="h-full w-full">
				{this.props.children}
			</div>
		);
	}
}
